import React, { FC, useContext, useEffect } from 'react';

import { navigate } from '@reach/router';

import { GlobalContext } from '../../../../store';
import { IAdminEvent, initialEventTemplate } from '../../../../store/admin/events/store';

import { InnerRouter } from '../../../../components/AnimatedRouter';
import Stepper from '../../../../components/Stepper';
import { IStep } from '../../../../components/Stepper/Stepper';
import { EVENT_SETUP_SECTIONS } from './Utils/EVENT_SETUP_SECTIONS';
import { getRouterDirection } from './Utils/getRouterDirection';

interface IProps {
  children: any;
  clubId: string;
  eventId: string;
}

const EventSetup: FC<IProps> = props => {
  const { clubId, children, eventId } = props;

  const {
    actions: {
      adminRoundFormats: { getMasterFormats, getRoundFormats, get: getRoundFormat }
    },
    adminEvents,
    adminEventEnrollments: eventEnrollments,
    adminInvitations: invitations
  } = useContext(GlobalContext);

  const event = adminEvents[Number(eventId)] || initialEventTemplate;
  const locationGroups = location
    ? /staff\/club\/[0-9]+\/event\/[0-9]+\/setup\/([a-z\-]+)/g.exec(location.pathname || '')
    : [];
  const currentPage = locationGroups && locationGroups.length > 1 ? locationGroups[1] : 'setup';

  useEffect(() => {
    const fetcher = async () => {
      if (eventId !== 'new') {
        await getRoundFormats(Number(clubId), eventId);
      }
      await getMasterFormats();
    };

    fetcher().catch();
  }, []);

  useEffect(() => {
    if ((event as any).rounds) {
      for (const round of (event as any).rounds) {
        getRoundFormat(clubId, (event as any).id, round.id);
      }
    }
  }, [eventId]);

  const setStep = (_: any, index: number) =>
    navigate(`/staff/club/${clubId}/event/${eventId}/setup/${EVENT_SETUP_SECTIONS[index]}`);

  const currentEvent = event as IAdminEvent;

  const hasInvitations =
    currentEvent.id &&
    ((invitations.eventMemberships[currentEvent.id] || []).length > 0 ||
      (eventEnrollments[currentEvent.id] || []).length > 0);

  interface IExtendedStep extends IStep {
    name: string;
    disabled?: boolean;
  }

  const steps: IExtendedStep[] = [
    { name: 'Getting Started', status: !!currentEvent.id ? 'done' : '' },
    {
      disabled: !currentEvent.id,
      name: 'Event Setup',
      status: !!currentEvent.totalPlayers && !!currentEvent.totalRounds ? 'done' : ''
    },
    {
      disabled: !currentEvent.id,
      name: 'Formats',
      status: currentEvent.totalRounds >= 1 ? 'done' : ''
    },
    {
      disabled: !currentEvent.id,
      name: 'Flights',
      // TODO: Determine status logic
      status: ''
    },
    {
      disabled: !currentEvent.id || !currentEvent.totalRounds,
      name: 'Round Details',
      status: !!currentEvent.totalRounds ? 'done' : ''
    },

    // Temporarily disable fees screen due to https://code.talpor.com/club15/frontend/-/issues/900
    // {
    //   disabled: !currentEvent.id,
    //   name: 'Entry Fees',
    //   status: !!(currentEvent.prices || []).length ? 'done' : ''
    // },
    {
      disabled: !currentEvent.id,
      name: 'Publish Event',
      status: (event as IAdminEvent).status === 'published' ? 'done' : ''
    },
    {
      disabled: !currentEvent.id,
      name: 'Invite Players',
      status: hasInvitations ? 'done' : ''
    }
  ];

  const activeStep = (EVENT_SETUP_SECTIONS as any)[currentPage]
    ? (EVENT_SETUP_SECTIONS as any)[currentPage]
    : 0;

  return (
    <>
      <Stepper steps={steps} active={activeStep} onChange={setStep} />

      <div className="admin-wrapper">
        <InnerRouter
          location={location}
          className="event-setup-router"
          direction={getRouterDirection}
        >
          {children || <div />}
        </InnerRouter>
      </div>
    </>
  );
};

export default EventSetup;
